import { getLocale } from "~/paraglide/runtime.js";

/**
 * Which plural form a count takes, for the two locales the site ships.
 *
 * Deliberately not `Intl.PluralRules`: the server ships English-only ICU data
 * while the browser has the full set, so the two disagree on Latvian and React
 * reports a hydration mismatch. This is the same reason `groupThousands` in
 * `format-number.ts` hand-rolls its separator. `getLocale()` resolves to the
 * same value on both sides of a request, so this is safe during SSR.
 *
 * Latvian takes three forms (CLDR):
 *   zero   0, 10, 11–19, 20, 30 … "10 piemēru"
 *   one    1, 21, 31 … but never 11 … "1 piemērs"
 *   other  everything else, 2–9 included … "5 piemēri"
 *
 * English takes two: one at exactly 1, other for everything else.
 */
export type PluralForm = "one" | "other" | "zero";

export function pluralForm(count: number): PluralForm {
  const n = Math.abs(Math.trunc(count));

  if (getLocale() === "lv") {
    const lastTwo = n % 100;
    if (n % 10 === 0 || (lastTwo >= 11 && lastTwo <= 19)) {
      return "zero";
    }
    return n % 10 === 1 ? "one" : "other";
  }

  return n === 1 ? "one" : "other";
}

/** Picks the message matching a count's plural form. */
export function plural(
  count: number,
  forms: Record<PluralForm, (input: { count: number }) => string>
): string {
  return forms[pluralForm(count)]({ count });
}
